import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";

import { ServiceNowIncidentStatus } from "../../../types/servicenow";

type Props = {
  status: ServiceNowIncidentStatus;
  onRetryNow?: () => void;
  retryEnabled?: boolean;
};

const formatRetryTime = (timestamp?: string | null): string | null => {
  if (!timestamp) {
    return null;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const ServiceNowErrorBanner: React.FC<Props> = ({ status, onRetryNow, retryEnabled = false }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const isExhausted = status.status === "EXHAUSTED";
  const message = isExhausted
    ? "ServiceNow Incident creation failed after multiple attempts. Please contact IT support."
    : "ServiceNow Incident creation failed. System will retry automatically.";
  const retryTime = !isExhausted ? formatRetryTime(status.nextRetryAt) : null;

  return (
    <View style={[
      styles.banner,
      isExhausted ? styles.bannerCritical : styles.bannerWarning,
      isDark && { backgroundColor: "#2B1200", borderColor: isExhausted ? "#7C2D12" : "#9A3412" },
    ]}>
      <View style={styles.iconWrap}>
        <MaterialIcons
          name={isExhausted ? "error-outline" : "sync-problem"}
          size={18}
          color={isDark ? "#F97316" : "#9A3412"}
        />
      </View>
      <View style={styles.content}>
        <Text style={[styles.message, isDark && { color: "#FED7AA" }]}>{message}</Text>
        {retryTime ? <Text style={[styles.meta, isDark && { color: "#FDBA74" }]}>Next retry: {retryTime}</Text> : null}
        {status.errorMessage ? <Text style={[styles.meta, isDark && { color: "#FDBA74" }]}>Last error: {status.errorMessage}</Text> : null}
      </View>
      <Pressable
        onPress={retryEnabled ? onRetryNow : undefined}
        disabled={!retryEnabled}
        style={({ pressed }) => [
          styles.retryButton,
          isDark && { backgroundColor: "#2B1200", borderColor: "#F97316" },
          !retryEnabled && styles.retryButtonDisabled,
          !retryEnabled && isDark && { backgroundColor: "#1a1a1a", borderColor: "#404040" },
          pressed && retryEnabled && styles.retryButtonPressed,
        ]}
      >
        <Text style={[styles.retryButtonText, isDark && { color: "#F97316" }, !retryEnabled && styles.retryButtonTextDisabled, !retryEnabled && isDark && { color: "#6E6E6E" }]}>Retry Now</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 12,
  },
  bannerWarning: {
    backgroundColor: "#FFF7ED",
    borderColor: "#FDBA74",
  },
  bannerCritical: {
    backgroundColor: "#FFF7ED",
    borderColor: "#F97316",
  },
  iconWrap: {
    paddingTop: 2,
  },
  content: {
    flex: 1,
    gap: 4,
  },
  message: {
    fontSize: 13,
    lineHeight: 18,
    color: "#7C2D12",
    fontWeight: "600",
  },
  meta: {
    fontSize: 12,
    lineHeight: 16,
    color: "#9A3412",
  },
  retryButton: {
    minWidth: 92,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#F97316",
  },
  retryButtonDisabled: {
    backgroundColor: "#F9FAFB",
    borderColor: "#D1D5DB",
  },
  retryButtonPressed: {
    opacity: 0.85,
  },
  retryButtonText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#C2410C",
  },
  retryButtonTextDisabled: {
    color: "#9CA3AF",
  },
});

export default ServiceNowErrorBanner;